import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/providers/AuthProvider";
import { MfaSetupDialog } from "@/components/auth/MfaSetupDialog";
import { authService } from "@/services";

const MFA_NOTICE_DISMISSED_KEY = "echopeak_mfa_notice_dismissed";

export function MfaNotice() {
  const { clientInfo, clientInfoLoading, getClientInfo } = useAuthContext();
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(MFA_NOTICE_DISMISSED_KEY) === "true";
  });
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [secretCode, setSecretCode] = useState("");
  const [session, setSession] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shouldShow =
    !clientInfoLoading && clientInfo && !clientInfo.isMFAEnabled && !isDismissed;

  const handleDismiss = () => {
    localStorage.setItem(MFA_NOTICE_DISMISSED_KEY, "true");
    setIsDismissed(true);
  };

  const handleSetupMfa = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.setupMfaAuthenticated();
      setSecretCode(response.secretCode);
      setSession(response.session);
      setShowMfaSetup(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to initialize MFA setup"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMfa = async (code: string): Promise<string[] | null> => {
    const response = await authService.verifyMfaSetupAuthenticated({
      session,
      code,
    });

    if (response.success && response.backupCodes) {
      return response.backupCodes;
    }

    throw new Error("Verification failed");
  };

  const handleMfaComplete = async () => {
    setShowMfaSetup(false);
    localStorage.setItem(MFA_NOTICE_DISMISSED_KEY, "true");
    setIsDismissed(true);
    await getClientInfo();
  };

  useEffect(() => {
    if (clientInfo?.isMFAEnabled) {
      setIsDismissed(true);
    }
  }, [clientInfo?.isMFAEnabled]);

  return (
    <>
      <AnimatePresence>
        {shouldShow && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-6"
          >
            <div className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-lg p-4">
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex items-start gap-4 pr-8">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Shield className="h-5 w-5 text-primary" />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm mb-1">
                    Secure Your Account
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    We encourage you to enable two-factor authentication (MFA)
                    to add an extra layer of security to your account.
                  </p>

                  {error && (
                    <div className="flex items-center gap-2 p-2 mb-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs">
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button
                    size="sm"
                    onClick={handleSetupMfa}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        <Shield className="h-3 w-3 mr-2" />
                        Enable MFA
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <MfaSetupDialog
        open={showMfaSetup}
        onOpenChange={setShowMfaSetup}
        secretCode={secretCode}
        email={clientInfo?.email ?? ""}
        onVerify={handleVerifyMfa}
        onComplete={handleMfaComplete}
      />
    </>
  );
}
